// frontend/src/pages/admin/EventQueuePage.tsx
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../../services/api";
import { ArrowLeft, Trash2, QrCode, AlertCircle, Monitor } from "lucide-react";
import { TimeAdjustmentControls } from "../../components/TimeAdjustmentControls";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { CompleteSessionModal } from "../../components/CompleteSessionModal";
import QRCodeModal from "../../components/QRCodeModal";
import type { ClientStatusType } from "../../types";
import { useTranslation } from "react-i18next";
import YulqoFace from "../../components/ui/YulqoFace";
import styles from "./EventQueuePage.module.css";

export default function EventQueuePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    confirmText: t("common.confirm"),
    danger: false,
  });
  const [qrModal, setQrModal] = useState({ isOpen: false });
  const [completeModal, setCompleteModal] = useState<{
    isOpen: boolean;
    clientId: string;
    clientName: string;
    clientEmail?: string;
    clientPhone?: string;
    estimatedDuration: number;
  }>({ isOpen: false, clientId: "", clientName: "", estimatedDuration: 30 });

  // ── Queries ────────────────────────────────────────────────────────────────
  const {
    data: event,
    isLoading: loadingEvent,
    error: eventError,
  } = useQuery({
    queryKey: ["admin", "event", eventId],
    queryFn: () => api.getEvent(eventId!),
    enabled: !!eventId,
  });

  const { data: allQueue = [], isLoading: loadingQueue } = useQuery({
    queryKey: ["admin", "queue", event?.organizationId || event?.artistId],
    queryFn: () => api.getQueue((event!.organizationId || event!.artistId)!),
    enabled: !!(event?.organizationId || event?.artistId),
    refetchInterval: 10000,
  });

  const queue = allQueue.filter((c: any) => c.eventId === eventId);

  const invalidateQueue = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "queue"] });

  // ── Mutations ──────────────────────────────────────────────────────────────
  const updateStatusMutation = useMutation({
    mutationFn: ({
      clientId,
      status,
    }: {
      clientId: string;
      status: ClientStatusType;
    }) => api.updateClientStatus(clientId, status),
    onSuccess: invalidateQueue,
  });

  const removeClientMutation = useMutation({
    mutationFn: (clientId: string) => api.removeClient(clientId),
    onSuccess: () => {
      invalidateQueue();
      setModalState({
        isOpen: true,
        title: `✅ ${t("common.success")}`,
        message:
          t("queue.clientRemoved") ||
          "Le client a été retiré de la file d'attente",
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (data: {
      clientId: string;
      amountPaid: number;
      paymentMethod: string;
      deliveryMethod: string;
      actualDuration: number;
    }) =>
      api.completeClient(data.clientId, {
        amountPaid: data.amountPaid,
        paymentMethod: data.paymentMethod,
        deliveryMethod: data.deliveryMethod,
        actualDuration: data.actualDuration,
      }),
    onSuccess: () => {
      invalidateQueue();
      setCompleteModal((prev) => ({ ...prev, isOpen: false }));
      setModalState({
        isOpen: true,
        title: `✅ ${t("status.completed")}`,
        message:
          t("queue.sessionCompleted") ||
          "Session clôturée avec succès ! Le reçu a été envoyé.",
      });
    },
    onError: (error: any) => {
      setCompleteModal((prev) => ({ ...prev, isOpen: false }));
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message:
          error.response?.data?.error || error.message || "Erreur inconnue",
      });
    },
  });

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleTimeAdjustment = async (
    clientId: string,
    newDuration: number,
  ) => {
    try {
      await api.adjustClientDuration(clientId, newDuration);
      invalidateQueue();
    } catch (error) {
      setModalState({
        isOpen: true,
        title: `❌ ${t("common.error")}`,
        message: error instanceof Error ? error.message : "Erreur inconnue",
      });
    }
  };

  const handleStart = (clientId: string, clientName: string) => {
    setConfirmDialog({
      isOpen: true,
      title: t("queue.startTattoo") || "Iniciar turno",
      message:
        t("queue.startConfirm", { name: clientName }) ||
        `¿Quieres iniciar el turno de ${clientName}?`,
      confirmText: t("common.start") || "Commencer",
      danger: false,
      onConfirm: () =>
        updateStatusMutation.mutate({ clientId, status: "IN_PROGRESS" }),
    });
  };

  const handleComplete = (client: any) => {
    setCompleteModal({
      isOpen: true,
      clientId: client.id,
      clientName: client.name,
      clientEmail: client.email,
      clientPhone: client.phone,
      estimatedDuration:
        client.estimatedWaitTime || event?.avgTimePerClient || 30,
    });
  };

  const handleRemove = (clientId: string, clientName: string) => {
    setConfirmDialog({
      isOpen: true,
      title: t("common.delete") || "Retirer de la file",
      message: `${t("common.confirmDelete") || "Êtes-vous sûr de vouloir retirer"} ${clientName} ?`,
      confirmText: t("common.delete"),
      danger: true,
      onConfirm: () => removeClientMutation.mutate(clientId),
    });
  };

  // ── États de chargement / erreur ───────────────────────────────────────────
  if (loadingEvent || loadingQueue) {
    return (
      <div className={styles.page}>
        <p className={styles.empty}>{t("common.loading")}</p>
      </div>
    );
  }

  if (!event) {
    const errorMessage = eventError
      ? (eventError as any).response?.data?.error || (eventError as any).message
      : t("queue.eventNotFound");
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-sm w-full text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            {t("common.error") || "Erreur"}
          </h2>
          <p className="text-gray-500 text-sm mb-6">{errorMessage}</p>
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="w-full py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors"
          >
            {t("common.back") || "Retour"}
          </button>
        </div>
      </div>
    );
  }

  // ── Listes dérivées ────────────────────────────────────────────────────────
  const waitingClients = queue.filter((c: any) => c.status === "WAITING");
  const inProgressClients = queue.filter(
    (c: any) => c.status === "IN_PROGRESS",
  );
  const completedClients = queue.filter((c: any) => c.status === "COMPLETED");
  const current = inProgressClients[0];
  const nextWaiting = waitingClients[0];

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => navigate("/admin/dashboard")}>
        <ArrowLeft className="w-4 h-4" />
        {t("common.back")}
      </button>

      <div className={styles.head}>
        <div>
          <span className={styles.hand}>{event.location || t("brand.business")}</span>
          <h1>{event.name}</h1>
        </div>
        <div className={styles.pills}>
          <span className={styles.pill}>
            {t("look.codePill")} <b>{event.code}</b>
          </span>
          <button type="button" className={styles.pill} onClick={() => setQrModal({ isOpen: true })}>
            <QrCode className="w-4 h-4" />
            QR
          </button>
          <button
            type="button"
            className={styles.pill}
            onClick={() => navigate(`/admin/events/${eventId}/tv`)}
          >
            <Monitor className="w-4 h-4" />
            {t("look.tvScreen")}
          </button>
        </div>
      </div>

      <div className={styles.biz}>
        <section>
          <div className={styles.now}>
            <div className={styles.row}>
              <span>{t("look.nowServing")}</span>
              <span>{event.description || ""}</span>
            </div>
            <div className={styles.nowMain}>
              <span className={styles.bignum}>{current?.position ?? "—"}</span>
              <div>
                <div className={styles.who}>
                  {current?.name || "—"}
                  <span>{current?.email || t("status.inProgress")}</span>
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            className={styles.call}
            disabled={!nextWaiting || updateStatusMutation.isPending}
            onClick={() => nextWaiting && handleStart(nextWaiting.id, nextWaiting.name)}
          >
            <strong>{t("look.callNext")}</strong>
            <span className={styles.next}>
              {t("look.nextIs")} <b>{nextWaiting?.position ?? "—"}</b>
            </span>
          </button>

          <div className={styles.kpis}>
            <div>
              <span>{t("status.waiting")}</span>
              <b>{waitingClients.length}</b>
            </div>
            <div>
              <span>{t("status.inProgress")}</span>
              <b>{inProgressClients.length}</b>
            </div>
            <div>
              <span>{t("status.completed")}</span>
              <b>{completedClients.length}</b>
            </div>
          </div>
        </section>

        <section className={styles.side}>
          <div className={styles.listHead}>
            <h2>{t("look.inLine")}</h2>
            <span>{t("look.people", { n: waitingClients.length })}</span>
          </div>
          <ul className={styles.qlist}>
            {waitingClients.length === 0 ? (
              <li className={styles.empty}>{t("look.emptyQueue")}</li>
            ) : (
              waitingClients.map((client: any, i: number) => (
                <li key={client.id} className={i === 0 ? styles.first : undefined}>
                  <YulqoFace index={client.position ?? i} size={28} />
                  <span className={styles.n}>{client.position ?? "—"}</span>
                  <span className={styles.nm}>
                    {client.name}
                    <span>{client.email}</span>
                  </span>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.start}
                      onClick={() => handleStart(client.id, client.name)}
                      disabled={updateStatusMutation.isPending}
                    >
                      {t("common.start")}
                    </button>
                    <button
                      type="button"
                      className={styles.remove}
                      onClick={() => handleRemove(client.id, client.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>

          {inProgressClients.map((client: any) => (
            <div key={client.id} style={{ marginTop: 16 }}>
              <button
                type="button"
                className={styles.done}
                style={{ width: "100%", padding: "12px" }}
                onClick={() => handleComplete(client)}
              >
                {t("queue.finishSession")} · {client.name}
              </button>
              <TimeAdjustmentControls
                clientId={client.id}
                currentTime={client.estimatedDuration || 30}
                clientName={client.name}
                onAdjust={(newDuration) => handleTimeAdjustment(client.id, newDuration)}
              />
            </div>
          ))}

          <button type="button" className={styles.qrCard} onClick={() => setQrModal({ isOpen: true })}>
            <QrCode className="w-10 h-10" />
            <div>
              <b>{t("look.printQr")}</b>
              <p>{t("look.printQrDesc")}</p>
            </div>
          </button>
        </section>
      </div>

      {/* ── Modales ── */}
      <ConfirmationModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        danger={confirmDialog.danger}
      />

      <CompleteSessionModal
        isOpen={completeModal.isOpen}
        onClose={() => setCompleteModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={(data) => {
          completeMutation.mutate({
            clientId: completeModal.clientId,
            ...data,
          });
        }}
        clientName={completeModal.clientName}
        clientEmail={completeModal.clientEmail}
        clientPhone={completeModal.clientPhone}
        estimatedDuration={completeModal.estimatedDuration}
        isLoading={completeMutation.isPending}
        defaultDeliveryMethod={event?.defaultDeliveryMethod}
        autoSendInvoice={event?.autoSendInvoice}
      />

      <QRCodeModal
        isOpen={qrModal.isOpen}
        onClose={() => setQrModal({ isOpen: false })}
        qrCodeUrl={event?.qrCodeUrl || ""}
        eventName={event?.name || ""}
        eventCode={event?.code || ""}
      />
    </div>
  );
}
